
import { useCallback } from 'react';
import { useUniversalRealtime } from './useUniversalRealtime';
import { 
  EntityRealtimeConfig, 
  RealtimeEventHandler 
} from '@/services/realtime/RealtimeService';

export type EntityType = 'poll' | 'card' | 'comment' | 'user' | 'country';

export interface EntityRealtimeHandlers<T = any> {
  onEntityUpdate?: (entity: T) => void;
  onEntityDelete?: (entityId: string) => void;
  onRelatedUpdate?: (payload: any) => void;
  onError?: (error: any) => void;
}

const getEntityConfig = (entityType: EntityType, entityId: string): EntityRealtimeConfig => {
  switch (entityType) {
    case 'poll':
      return {
        entityType,
        entityId,
        tables: [
          { table: 'polls', event: 'UPDATE', filter: `id=eq.${entityId}` },
          { table: 'poll_votes', event: '*', filter: `poll_id=eq.${entityId}` },
          { table: 'anonymous_poll_votes', event: '*', filter: `poll_id=eq.${entityId}` },
          { table: 'poll_comments', event: '*', filter: `poll_id=eq.${entityId}` },
          { table: 'poll_likes', event: '*', filter: `poll_id=eq.${entityId}` },
          { table: 'poll_shares', event: '*', filter: `poll_id=eq.${entityId}` }
        ]
      };
    
    case 'card':
      return {
        entityType,
        entityId,
        tables: [
          { table: 'insight_cards', event: 'UPDATE', filter: `id=eq.${entityId}` },
          { table: 'card_comments', event: '*', filter: `card_id=eq.${entityId}` },
          { table: 'card_likes', event: '*', filter: `card_id=eq.${entityId}` },
          { table: 'card_shares', event: '*', filter: `card_id=eq.${entityId}` }
        ]
      };
    
    case 'comment':
      return {
        entityType,
        entityId,
        tables: [
          { table: 'poll_comments', event: '*', filter: `id=eq.${entityId}` },
          { table: 'card_comments', event: '*', filter: `id=eq.${entityId}` },
          { table: 'poll_comment_votes', event: '*', filter: `comment_id=eq.${entityId}` },
          { table: 'card_comment_votes', event: '*', filter: `comment_id=eq.${entityId}` }
        ]
      };
    
    case 'user':
      return {
        entityType,
        entityId,
        tables: [
          { table: 'profiles', event: 'UPDATE', filter: `id=eq.${entityId}` },
          { table: 'user_id_documents', event: '*', filter: `user_id=eq.${entityId}` }
        ]
      };
    
    case 'country':
      return {
        entityType,
        entityId,
        tables: [
          { table: 'countries', event: 'UPDATE', filter: `code=eq.${entityId}` }
        ]
      };
    
    default:
      throw new Error(`Unsupported entity type: ${entityType}`);
  }
};

export const useEntityRealtime = <T = any>(
  entityType: EntityType,
  entityId: string,
  handlers: EntityRealtimeHandlers<T>,
  enabled: boolean = true
) => {
  const entityConfig = getEntityConfig(entityType, entityId);
  
  const realtimeHandlers: RealtimeEventHandler = {
    onInsert: useCallback((payload) => {
      console.log(`[useEntityRealtime] Insert for ${entityType}:${entityId}:`, payload);
      handlers.onRelatedUpdate?.(payload);
    }, [entityType, entityId, handlers]),
    
    onUpdate: useCallback((payload) => {
      console.log(`[useEntityRealtime] Update for ${entityType}:${entityId}:`, payload);
      
      // Check if this is the main entity being updated
      if (payload.table === getMainTable(entityType) && payload.new?.id === entityId) {
        handlers.onEntityUpdate?.(payload.new);
      } else {
        handlers.onRelatedUpdate?.(payload);
      }
    }, [entityType, entityId, handlers]),
    
    onDelete: useCallback((payload) => {
      console.log(`[useEntityRealtime] Delete for ${entityType}:${entityId}:`, payload);
      
      // Check if the main entity was deleted
      if (payload.table === getMainTable(entityType) && payload.old?.id === entityId) {
        handlers.onEntityDelete?.(entityId);
      } else {
        handlers.onRelatedUpdate?.(payload);
      }
    }, [entityType, entityId, handlers]),
    
    onError: handlers.onError
  };

  const realtimeState = useUniversalRealtime(
    {
      channelId: `entity-${entityType}-${entityId}`,
      configs: entityConfig.tables,
      enabled
    },
    realtimeHandlers
  );

  return {
    ...realtimeState,
    entityType,
    entityId
  };
};

const getMainTable = (entityType: EntityType): string => {
  switch (entityType) {
    case 'poll': return 'polls';
    case 'card': return 'insight_cards';
    case 'comment': return 'poll_comments'; // or 'card_comments'
    case 'user': return 'profiles';
    case 'country': return 'countries';
    default: throw new Error(`Unknown entity type: ${entityType}`);
  }
};
